import { useState } from "react";
import Forms, { DEFAULT_FORM_ID } from "@/components/templateParts/Footer/Forms/Forms";
import { motion } from "framer-motion";
import "./styles.scss";
import useStore from "@/hooks/useStore";
import clsx from "clsx";
import { SubmitButton } from "@/components/utils/SubmitButton";
import { CONTACT_ID } from "@/utils/appConstants";
import { ContactFormImagesPropTypes } from "./propTypes";

/* #META
 {
  "NAME": "Contact Form Images",
  "CLASSNAME": "form-images-section",
  "URL": "http://nicasource.local"
 }
#ENDMETA */

const ContactFormImages = ({ data }) => {
  const {
    section_title: title,
    new_front_img: newFrontImg,
    new_back_img: newBackImg,
    join_front_img: joinFrontImg,
    join_back_img: joinBackImg,
    general_front_img: generalFrontImg,
    general_back_img: generalBackImg,
    new_project_title: newProjectTitle,
    join_team_title: joinTeamTitle,
    general_form_title: generalFormTitle,
  } = data;

  const { acfOptions } = useStore();
  const siteOptions = acfOptions();
  const { footer_info: footerInfo } = siteOptions;

  const { contact_forms: contactForms } = footerInfo || {};

  const [selectedForm, setSelectedForm] = useState(DEFAULT_FORM_ID);

  return (
    <section className="form-images-section">
      <div className="form-images-container relative pt-0 lg:pt-[80px] flex flex-col lg:flex-row xl:pt-0">
        {/* Contact */}
        {contactForms && (
          <div
            id={CONTACT_ID}
            className="bg-darkGrayRevamp justify-center items-center h-auto order-2 flex flex-col gap-6 w-full p-4 pt-6 lg:w-1/2 lg:p-16 lg:order-1"
          >
            <div className="w-full lg:max-w-xl">
              {title && (
                <div
                  className="text-white section-title"
                  dangerouslySetInnerHTML={{ __html: title }}
                />
              )}
              {contactForms?.length && (
                <div className="flex items-start gap-6 flex-row lg:mt-6">
                  {contactForms.map((form, index) => {
                    const formId = Number(form?.form_id ?? DEFAULT_FORM_ID);
                    const isActive = formId === selectedForm;

                    return (
                      <SubmitButton
                        action={() => setSelectedForm(formId)}
                        key={index}
                        name={form.form_title}
                        className={clsx(
                          "form-images-button border-b-2 text-sm transition-all duration-300 lg:text-lg",
                          isActive
                            ? "border-b-honey text-honey font-bold pointer-events-none"
                            : "border-b-transparent text-light hover:text-honey focus:border-b-honey"
                        )}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {Boolean(selectedForm) && (
              <motion.div
                key={selectedForm}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="w-full lg:max-w-xl"
              >
                <Forms formId={selectedForm} />
              </motion.div>
            )}
          </div>
        )}

        {data && (
          <motion.div
            key={selectedForm}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="form-image-container flex flex-col relative w-full order-1 flex-1 lg:w-1/2 lg:order-2"
          >
            {(() => {
              switch (selectedForm) {
                case 1:
                  return (
                    <div className="right-container">
                      {newBackImg && (
                        <img className="back-img" src={newBackImg?.url} alt={newBackImg?.alt} />
                      )}
                      {newFrontImg && (
                        <img className="front-img" src={newFrontImg?.url} alt={newFrontImg?.alt} />
                      )}
                      <div className="form-info-container">
                        {newProjectTitle && (
                          <div
                            className="text-white"
                            dangerouslySetInnerHTML={{ __html: newProjectTitle }}
                          />
                        )}
                      </div>
                    </div>
                  );
                case 2:
                  return (
                    <div className="right-container">
                      {joinBackImg && (
                        <img className="back-img" src={joinBackImg?.url} alt={joinBackImg?.alt} />
                      )}
                      {joinFrontImg && (
                        <img
                          className="front-img"
                          src={joinFrontImg?.url}
                          alt={joinFrontImg?.alt}
                        />
                      )}
                      <div className="form-info-container">
                        {joinTeamTitle && (
                          <div
                            className="text-white"
                            dangerouslySetInnerHTML={{ __html: joinTeamTitle }}
                          />
                        )}
                      </div>
                    </div>
                  );
                case 3:
                  return (
                    <div className="right-container">
                      {generalBackImg && (
                        <img
                          className="back-img"
                          src={generalBackImg?.url}
                          alt={generalBackImg?.alt}
                        />
                      )}
                      {generalFrontImg && (
                        <img
                          className="front-img"
                          src={generalFrontImg?.url}
                          alt={generalFrontImg?.alt}
                        />
                      )}
                      <div className="form-info-container">
                        {generalFormTitle && (
                          <div
                            className="text-white"
                            dangerouslySetInnerHTML={{ __html: generalFormTitle }}
                          />
                        )}
                      </div>
                    </div>
                  );
                default:
                  return null;
              }
            })()}
          </motion.div>
        )}
      </div>
    </section>
  );
};

ContactFormImages.propTypes = ContactFormImagesPropTypes;

export default ContactFormImages;
